import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash, PencilSimple, Buildings } from "@phosphor-icons/react";
import api, { formatApiError, listInvites, revokeInvite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { initials, ROLE_LABELS, GLOBAL_ROLES } from "../lib/utils";

const INTERNAL_ROLES = ["super_admin", "project_manager", "team_member"];
const CLIENT_ROLES = ["client_admin", "client_user"];

const ROLE_TONE = {
  super_admin: "bg-rose-50 text-rose-700 border-rose-200",
  project_manager: "bg-blue-50 text-blue-700 border-blue-200",
  team_member: "bg-violet-50 text-violet-700 border-violet-200",
  client_admin: "bg-amber-50 text-amber-700 border-amber-200",
  client_user: "bg-slate-100 text-slate-700 border-slate-200",
};

/* ---------- Invites table ---------- */
function InvitesTable({ invites, onRevoke }) {
  return (
    <div className="surface-card overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full text-sm table-tight" data-testid="invites-table">
        <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-[0.1em] text-slate-500">
          <tr><th>Email</th><th>Role</th><th>Client</th><th>Project</th><th>Invited by</th><th></th></tr>
        </thead>
        <tbody>
          {invites.length === 0 && <tr><td colSpan="6" className="text-center text-slate-400 py-10">No pending invitations.</td></tr>}
          {invites.map((inv) => (
            <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`invite-row-${inv.id}`}>
              <td className="font-mono text-[12px] text-slate-700">{inv.email}</td>
              <td>{ROLE_LABELS[inv.role]}</td>
              <td className="text-slate-600">{inv.organization_name || (GLOBAL_ROLES.includes(inv.role) ? "All clients" : "—")}</td>
              <td className="text-slate-600">{inv.organization_name ? (inv.all_projects ? "All projects" : (inv.project_name || "—")) : (GLOBAL_ROLES.includes(inv.role) ? "All projects" : "—")}</td>
              <td className="text-slate-600">{inv.invited_by_name || "—"}</td>
              <td className="text-right">
                <button onClick={() => onRevoke(inv.id)} className="text-slate-400 hover:text-rose-600 text-[11px] font-medium" data-testid={`invite-revoke-${inv.id}`}>
                  Revoke
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function Users() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);

  const canManage = ["super_admin", "project_manager", "client_admin"].includes(user.role);
  const showInternalTab = ["super_admin", "project_manager", "team_member"].includes(user.role);

  const internalUsers = useMemo(() => users.filter((u) => INTERNAL_ROLES.includes(u.role)), [users]);
  const clientUsers = useMemo(() => users.filter((u) => CLIENT_ROLES.includes(u.role)), [users]);

  const refresh = () => api.get("/users").then((r) => setUsers(r.data));
  const refreshInvites = () => canManage && listInvites().then(setInvites).catch(() => {});

  useEffect(() => {
    refresh();
    refreshInvites();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); refresh(); }
    catch (err) { window.alert(formatApiError(err.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !(u.is_active !== false) }); refresh(); }
    catch (err) { window.alert(formatApiError(err.response?.data?.detail)); }
  };

  const accessSummary = (u) => {
    if (GLOBAL_ROLES.includes(u.role)) return <span className="text-slate-400 text-xs italic">All clients &amp; projects</span>;
    const a = u.assignments || [];
    if (!a.length) return <span className="text-rose-400 text-xs italic">No access</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {a.map((x) => (
          <span key={x.organization_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-slate-200 bg-white text-slate-600">
            <Buildings size={10} className="text-blue-500" />
            {x.organization_name || "Client"}
            <span className="text-slate-400">· {x.all_projects ? "all projects" : `${(x.project_names || []).length || x.project_ids?.length || 0} project(s)`}</span>
          </span>
        ))}
      </div>
    );
  };

  /* ---------- Users table (reused for internal + client tabs) ---------- */
  function UsersTable({ rows }) {
    return (
      <>
        {/* Desktop table — md+ */}
        <div className="hidden md:block surface-card overflow-x-auto">
          <table className="w-full text-sm table-tight" data-testid="users-table">
            <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-[0.1em] text-slate-500">
              <tr><th>Name</th><th>Role</th><th>Client &amp; project access</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="5" className="text-center text-slate-400 py-10">No users.</td></tr>}
              {rows.map((u) => {
                const active = u.is_active !== false;
                return (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50 align-top" data-testid={`user-row-${u.id}`}>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-semibold">{initials(u.name)}</div>
                        <div>
                          <Link to={`/users/${u.id}`} className="font-medium text-slate-800 hover:text-blue-600 hover:underline">{u.name}</Link>
                          <div className="text-slate-500 font-mono text-[11px]">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border uppercase tracking-wide ${ROLE_TONE[u.role]}`}>
                        {ROLE_LABELS[u.role]}
                      </span>
                    </td>
                    <td className="max-w-md">{accessSummary(u)}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${active ? "text-emerald-600" : "text-slate-400"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {canManage && (
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => navigate(`/users/${u.id}/edit`)} className="text-slate-400 hover:text-blue-600" title="Edit" data-testid={`user-edit-${u.id}`}>
                            <PencilSimple size={15} />
                          </button>
                          {u.id !== user.id && (
                            <button onClick={() => toggleActive(u)} className="text-slate-400 hover:text-amber-600 text-[11px] font-medium" title={active ? "Disable" : "Enable"}>
                              {active ? "Disable" : "Enable"}
                            </button>
                          )}
                          {u.id !== user.id && (
                            <button onClick={() => remove(u.id)} className="text-slate-400 hover:text-rose-600" title="Delete" data-testid={`user-delete-${u.id}`}>
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards — below md */}
        <div className="md:hidden space-y-2">
          {rows.length === 0 && (
            <div className="text-center text-slate-400 py-10 surface-card">No users.</div>
          )}
          {rows.map((u) => {
            const active = u.is_active !== false;
            return (
              <div key={u.id} className="surface-card p-3 space-y-2.5" data-testid={`user-card-${u.id}`}>
                {/* Avatar + name + email */}
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-semibold shrink-0">{initials(u.name)}</div>
                  <div className="min-w-0">
                    <Link to={`/users/${u.id}`} className="font-medium text-slate-800 hover:text-blue-600 hover:underline truncate block">{u.name}</Link>
                    <div className="text-slate-500 font-mono text-[11px] truncate">{u.email}</div>
                  </div>
                </div>

                {/* Role badge + status dot */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border uppercase tracking-wide ${ROLE_TONE[u.role]}`}>
                    {ROLE_LABELS[u.role]}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${active ? "text-emerald-600" : "text-slate-400"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-300"}`} />
                    {active ? "Active" : "Disabled"}
                  </span>
                </div>

                {/* Access summary */}
                <div>{accessSummary(u)}</div>

                {/* Actions */}
                {canManage && (
                  <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
                    <button type="button" onClick={() => navigate(`/users/${u.id}/edit`)} className="text-slate-400 hover:text-blue-600" title="Edit" data-testid={`user-edit-card-${u.id}`}>
                      <PencilSimple size={15} />
                    </button>
                    {u.id !== user.id && (
                      <button type="button" onClick={() => toggleActive(u)} className="text-slate-400 hover:text-amber-600 text-[11px] font-medium" title={active ? "Disable" : "Enable"}>
                        {active ? "Disable" : "Enable"}
                      </button>
                    )}
                    {u.id !== user.id && (
                      <button type="button" onClick={() => remove(u.id)} className="text-slate-400 hover:text-rose-600" title="Delete" data-testid={`user-delete-card-${u.id}`}>
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Access management</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Team &amp; Users</h1>
          <p className="text-sm text-slate-500 mt-1">Manage members and assign them to clients and projects.</p>
        </div>
        {canManage && (
          <Button onClick={() => navigate("/users/new")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-user-button">
            <Plus size={14} weight="bold" className="mr-1.5" /> Add user
          </Button>
        )}
      </div>

      <Tabs defaultValue={showInternalTab ? "internal" : "clients"} className="w-full">
        <TabsList>
          {showInternalTab && <TabsTrigger value="internal" data-testid="tab-internal">Internal team ({internalUsers.length})</TabsTrigger>}
          <TabsTrigger value="clients" data-testid="tab-clients">Client users ({clientUsers.length})</TabsTrigger>
          {canManage && <TabsTrigger value="invites" data-testid="tab-invites">Pending invitations ({invites.length})</TabsTrigger>}
        </TabsList>
        {showInternalTab && <TabsContent value="internal"><UsersTable rows={internalUsers} /></TabsContent>}
        <TabsContent value="clients"><UsersTable rows={clientUsers} /></TabsContent>
        {canManage && (
          <TabsContent value="invites">
            <InvitesTable invites={invites} onRevoke={async (id) => {
              if (!window.confirm("Revoke this invitation?")) return;
              try { await revokeInvite(id); refreshInvites(); }
              catch (err) { window.alert(formatApiError(err.response?.data?.detail)); }
            }} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
